// Module-level (not per-call to toast()) so every caller shares the same
// record of which currently-visible toasts are success toasts, keyed by id.
let successToastCounter = 0;
const activeSuccessToastIds = new Set();

// Success toasts land here the instant they're shown and stay until they've had
// one animation frame to actually render (see showToast). hideAllSuccessToasts
// skips anything still in this set, so a route change can't dismiss the very
// toast that the SAME showToast(...); navigate(...) call just raised — the
// dominant confirm-then-redirect pattern in this app (e.g. JobDetails.tsx's
// "job deleted" toast). A pending id stays tracked in activeSuccessToastIds and
// is swept normally on whichever later navigation actually follows it.
//
// This is a fixed one-frame window, not true navigation-scoped ownership — it
// protects a call site only as long as showToast() and the navigate() that
// follows it stay within the same frame (a synchronous handler, or an awaited
// chain that resolves within a frame, both qualify; every current call site
// does). It would stop working for a call site with real async work between
// the two — e.g. showToast(...); await dispatch(someApiCall()); navigate(...)
// — and for react-router's v7_startTransition (already logged as a pending
// future-flag upgrade), which can defer a route commit past a frame.
const pendingRenderSuccessToastIds = new Set();

// <s-toast> fires this on every dismissal — its own dismiss control, a
// programmatic dismissToast()/dismissAllToasts() call, or a future timer —
// so this is the one place that reliably keeps activeSuccessToastIds from
// accumulating ids for toasts the user closed themselves; hideToast/hideAllToast
// below only cover the two JS-driven paths. Several other Skylab components
// (s-banner, s-alert, s-chip, s-dialog, s-select, aui-sidenav) also emit
// s-dismiss for their own reasons, so the tagName check keeps this from
// reacting to (or id-colliding with) any of those.
//
// Registered once here at module import, not inside toast() (which runs fresh
// on every render across many components) — so importing this module has a
// side effect on `document` for the lifetime of the page.
document.addEventListener("s-dismiss", event => {
    if (event.target.tagName !== "S-TOAST") return;
    const dismissedId = event.detail?.id;
    activeSuccessToastIds.delete(dismissedId);
    pendingRenderSuccessToastIds.delete(dismissedId);
});

function toast() {
    function showToast(status, text, id, persistent = true, actionText, actionHandler, details) {
        // Success toasts need a real id to be individually dismissable on route
        // change (see hideAllSuccessToasts), even when the caller didn't supply one.
        const toastId = status === "success" && !id ? `success-toast-${++successToastCounter}` : id;
        document.querySelector("s-toasts").createToast({
            status,
            text,
            id: toastId,
            persistent,
            actionText,
            actionHandler,
            details,
        });
        if (status === "success") {
            activeSuccessToastIds.add(toastId);
            pendingRenderSuccessToastIds.add(toastId);
            // Registered after createToast() so this runs on the frame after
            // Stencil's own DOM-write rAF (queued synchronously by the call
            // above), guaranteeing the toast has actually rendered once this
            // fires and the id is no longer "pending".
            requestAnimationFrame(() => {
                pendingRenderSuccessToastIds.delete(toastId);
            });
        }
    }

    function hideAllToast() {
        activeSuccessToastIds.clear();
        pendingRenderSuccessToastIds.clear();
        document.querySelector("s-toasts").dismissAllToasts();
    }

    function hideToast(toastID) {
        activeSuccessToastIds.delete(toastID);
        pendingRenderSuccessToastIds.delete(toastID);
        document.querySelector("s-toasts").dismissToast(toastID);
    }

    /**
     * Dismisses the currently-visible success toasts that predate this
     * navigation, leaving error/warning/info toasts on screen (an error is
     * worth keeping until the user has actually seen it) and leaving alone any
     * success toast still in pendingRenderSuccessToastIds — see that set's
     * comment for why.
     */
    function hideAllSuccessToasts() {
        const idsToClear = Array.from(activeSuccessToastIds).filter(
            id => !pendingRenderSuccessToastIds.has(id)
        );
        idsToClear.forEach(id => activeSuccessToastIds.delete(id));
        const toasts = document.querySelector("s-toasts");
        idsToClear.forEach(id => toasts?.dismissToast(id));
    }

    return [showToast, hideToast, hideAllToast, hideAllSuccessToasts];
}

export default toast;
